import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, TenancyModal, Th } from '../platform/_ui';
import { orgSupportApi } from '../api/org-support.api.js';

/* C-02: Documentation portal is not yet published. Cards are informational only —
   no href, no cursor:pointer — until real URLs are available. */
const DOCS_LINKS = [
  { title: 'Getting Started',       desc: 'Set up your organization and first workspace.',    icon: 'PlayCircle',    color: 'var(--nx-accent, #125AB0)' },
  { title: 'API Reference',         desc: 'Full API documentation with examples.',            icon: 'Code',          color: 'var(--nx-accent)' },
  { title: 'Member Management',     desc: 'Invite, manage, and assign roles to members.',     icon: 'Users',         color: 'var(--nx-accent)' },
  { title: 'Workspace Guide',       desc: 'Create and configure workspaces.',                 icon: 'LayoutGrid',    color: 'var(--nx-accent)' },
  { title: 'License Management',    desc: 'Allocate and manage user licenses.',               icon: 'Key',           color: 'var(--nx-accent)' },
  { title: 'Security & SSO',        desc: 'Configure MFA, SSO, and security policies.',      icon: 'Shield',        color: 'var(--nx-accent)' },
  { title: 'Branding & White Label',desc: 'Customize the look and feel of your platform.',   icon: 'Eye',           color: 'var(--nx-accent)' },
  { title: 'Developer Center',      desc: 'API keys, webhooks, and integrations.',            icon: 'Wrench',        color: 'var(--nx-accent)' },
];

const FAQS = [
  { q: 'How do I invite a new member to my organization?',
    a: 'Go to Members → click "Invite Member" → enter email and role. An invitation link will be sent automatically.' },
  { q: 'Can a user belong to multiple workspaces?',
    a: 'Yes. A single organization member can be added to multiple workspaces with different roles in each.' },
  { q: 'How do I transfer the Organization Owner role?',
    a: 'Contact your platform administrator. The Organization Owner role cannot be transferred without SA assistance.' },
  { q: 'What happens when a license expires?',
    a: 'Expired licenses enter a 30-day grace period. After that, affected users will lose access until the license is renewed.' },
  { q: 'How do I reset a member password?',
    a: 'Go to Members → select the member → click "Reset Password" in the detail panel. A reset link is sent to their email.' },
];

const TICKET_PRIORITY = ['Low', 'Medium', 'High', 'Critical'];
const TICKET_CATEGORY = ['Technical Issue', 'Billing', 'Feature Request', 'Account Access', 'Integration', 'Other'];
const fmt     = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtEnum = (s)   => s ? s.replaceAll('_', ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : '—';

const STATUS_TONE  = { OPEN: 'blue', IN_PROGRESS: 'amber', RESOLVED: 'green', CLOSED: 'gray' };
const PRIORITY_TONE = { LOW: 'gray', MEDIUM: 'blue', HIGH: 'amber', CRITICAL: 'red' };

export default function OrgSupport({ data }) {
  const { orgId } = useParams();
  const navigate  = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel') || 'Back';
  const toast = useToast();

  const section    = searchParams.get('section') || 'overview';
  const setSection = (id) => {
    const p = new URLSearchParams(searchParams);
    p.set('section', id);
    setSearchParams(p, { replace: true });
  };
  const [ticketModal, setTicketModal] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ OPEN: 0, IN_PROGRESS: 0, RESOLVED: 0, CLOSED: 0 });
  const [faqOpen, setFaqOpen] = useState(null);
  const [form, setForm] = useState({ subject: '', category: 'Technical Issue', priority: 'Medium', description: '' });
  const [ticketDetail, setTicketDetail] = useState(null);
  const [replyText, setReplyText]       = useState('');
  const [submitting, setSubmitting]     = useState(false);
  const [replying, setReplying]         = useState(false);
  const [docsSearch, setDocsSearch]     = useState('');

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([orgSupportApi.listTickets(orgId), orgSupportApi.stats(orgId)]);
      setTickets(t || []);
      setStats(s || { OPEN: 0, IN_PROGRESS: 0, RESOLVED: 0, CLOSED: 0 });
    } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load support data' }); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const org = data.state.organizations.find(o => o.id === orgId);
  if (!org) return null;

  const handleSubmitTicket = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast({ tone: 'warning', icon: 'AlertCircle', title: 'Subject and description are required' });
      return;
    }
    setSubmitting(true);
    try {
      const ticket = await orgSupportApi.createTicket(orgId, {
        subject: form.subject,
        description: form.description,
        category: form.category,
        priority: form.priority.toUpperCase(),
      });
      setTickets(t => [ticket, ...t]);
      setStats(s => ({ ...s, OPEN: (s.OPEN || 0) + 1 }));
      toast({ tone: 'success', icon: 'Check', title: 'Ticket submitted', desc: 'Our team will respond within 24 hours.' });
      setTicketModal(false);
      setForm({ subject: '', category: 'Technical Issue', priority: 'Medium', description: '' });
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to submit ticket', desc: e.message });
    } finally { setSubmitting(false); }
  };

  const handleViewTicket = async (t) => {
    try {
      const detail = await orgSupportApi.getTicket(orgId, t.id);
      setTicketDetail(detail || t);
    } catch {
      setTicketDetail(t);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !ticketDetail) return;
    setReplying(true);
    try {
      const msg = await orgSupportApi.addMessage(orgId, ticketDetail.id, replyText.trim());
      setTicketDetail(d => ({ ...d, messages: [...(d.messages || []), msg] }));
      setReplyText('');
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to send message', desc: e.message });
    } finally { setReplying(false); }
  };

  const NAV = [
    { id: 'overview',      label: 'Overview',         icon: 'Home'       },
    { id: 'docs',          label: 'Documentation',    icon: 'BookOpen'   },
    { id: 'releases',      label: 'Release Notes',    icon: 'Tag'        },
    { id: 'status',        label: 'System Status',    icon: 'Activity'   },
    { id: 'faq',           label: 'FAQ',              icon: 'HelpCircle' },
    { id: 'training',      label: 'Training Videos',  icon: 'Video'      },
    { id: 'tickets',       label: 'My Tickets',       icon: 'Inbox'      },
    { id: 'downloads',     label: 'Downloads',        icon: 'Download'   },
  ];

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Support Center"
        sub={'Resources and support for ' + org.name}
        back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined}
        actions={<Btn variant="primary" icon="MessageCircle" onClick={() => setTicketModal(true)}>Raise Ticket</Btn>}
      />

      {section === 'tickets' && (
        <div className="nx-cc-stat-grid nx-kpi-4">
          <StatCard label="Open Tickets"     value={stats.OPEN || 0}        icon="Inbox"       color="amber"  />
          <StatCard label="In Progress"      value={stats.IN_PROGRESS || 0} icon="Clock"       color="blue"   />
          <StatCard label="Resolved"         value={stats.RESOLVED || 0}    icon="CheckCircle" color="green"  />
          <StatCard label="Closed"           value={stats.CLOSED || 0}      icon="Archive"     color="gray"   />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 16 }}>
        {/* Nav */}
        <div className="nx-cc-chart-card" style={{ padding: 0, height: 'fit-content' }}>
          {NAV.map(n => (
            <button key={n.id} onClick={() => setSection(n.id)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: 'none', cursor: 'pointer', textAlign: 'left', background: section === n.id ? 'var(--nx-accent-tint)' : 'transparent', borderLeft: '2px solid ' + (section === n.id ? 'var(--nx-accent)' : 'transparent') }}>
              <NexusIcon name={n.icon} size={14} style={{ color: section === n.id ? 'var(--nx-accent)' : 'var(--nx-text-muted)', flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: section === n.id ? 600 : 400, color: section === n.id ? 'var(--nx-text)' : 'var(--nx-text-muted)' }}>{n.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div>
          {section === 'overview' && (
            <div>
              {/* C-02: Cards are informational only — documentation portal not yet published. No cursor:pointer on non-interactive elements. */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 12, marginBottom: 16 }}>
                {DOCS_LINKS.slice(0, 4).map(d => (
                  <div key={d.title} className="nx-cc-chart-card" style={{ padding: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ '--_sc': d.color, width: 32, height: 32, borderRadius: 8, background: 'color-mix(in srgb, var(--_sc) 9%, transparent)', color: 'var(--_sc)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <NexusIcon name={d.icon} size={16} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{d.title}</div>
                        <div className="nx-muted nx-mini">{d.desc}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <Btn variant="ghost" icon="MessageCircle" onClick={() => setTicketModal(true)}>Raise a Ticket</Btn>
                <Btn variant="ghost" icon="BookOpen" onClick={() => setSection('docs')}>Browse Docs</Btn>
              </div>
            </div>
          )}

          {section === 'docs' && (
            /* C-02: Documentation portal not yet published — show Coming Soon state.
               Cards below are informational only; no links are wired until the
               documentation site is available. */
            <div>
              <div className="nx-cc-chart-card" style={{ padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <NexusIcon name="BookOpen" size={18} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Documentation Portal — Coming Soon</div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>The documentation portal is being prepared. Contact your platform administrator or account manager for guidance in the meantime.</div>
                </div>
              </div>
              <input
                className="nx-input"
                placeholder="Search documentation…"
                value={docsSearch}
                onChange={e => setDocsSearch(e.target.value)}
                style={{ width: '100%', maxWidth: 340, marginBottom: 12 }}
                aria-label="Search documentation topics"
              />
              {(() => {
                const q = docsSearch.trim().toLowerCase();
                const filtered = q
                  ? DOCS_LINKS.filter(d => d.title.toLowerCase().includes(q) || d.desc.toLowerCase().includes(q))
                  : DOCS_LINKS;
                if (filtered.length === 0) {
                  return (
                    <div className="nx-empty" style={{ flexDirection: 'column', gap: 6 }}>
                      <NexusIcon name="SearchX" size={20} />
                      <span>No results for "{docsSearch}"</span>
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 12 }}>
                    {filtered.map(d => (
                      <div key={d.title} className="nx-cc-chart-card" style={{ padding: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <div style={{ '--_sc': d.color, width: 32, height: 32, borderRadius: 8, background: 'color-mix(in srgb, var(--_sc) 9%, transparent)', color: 'var(--_sc)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <NexusIcon name={d.icon} size={16} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{d.title}</div>
                            <div className="nx-muted nx-mini">{d.desc}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {section === 'releases' && (
            /* C-03: Hardcoded internal engineering milestones removed.
               Release notes will be served from the platform changelog API
               when available. */
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 6 }}>
              <NexusIcon name="Tag" size={22} />
              <span style={{ fontWeight: 600 }}>Release Notes</span>
              <span className="nx-muted nx-mini">Platform release notes are published on the Nuacem changelog. Contact your account manager for the latest updates.</span>
            </div>
          )}

          {section === 'status' && (
            /* C-01: Live status monitoring is not available in this console.
               Do NOT show hardcoded "All Systems Operational" — it would
               actively mislead admins during real incidents. */
            <div className="nx-cc-chart-card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <NexusIcon name="Activity" size={18} style={{ color: 'var(--nx-text-muted)' }} />
                <span style={{ fontWeight: 700, fontSize: 14 }}>System Status</span>
              </div>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.6 }}>
                Real-time system status monitoring is managed by your platform operations team. This console does not have live access to service health data.
              </p>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.6 }}>
                For current system status and incident updates, contact your platform administrator.
              </p>
            </div>
          )}

          {section === 'faq' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {FAQS.map((f, i) => (
                <div key={i} className="nx-cc-chart-card" style={{ padding: 14, cursor: 'pointer' }} onClick={() => setFaqOpen(faqOpen === i ? null : i)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{f.q}</span>
                    <NexusIcon name={faqOpen === i ? 'ChevronUp' : 'ChevronDown'} size={14} />
                  </div>
                  {faqOpen === i && <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.6 }}>{f.a}</p>}
                </div>
              ))}
            </div>
          )}

          {section === 'training' && (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 6 }}>
              <NexusIcon name="Video" size={22} />
              <span style={{ fontWeight: 600 }}>Training Videos</span>
              <span className="nx-muted nx-mini">Video library coming soon. Contact your platform admin for in-person training.</span>
            </div>
          )}

          {section === 'tickets' && (
            <div>
              {tickets.length === 0 ? (
                <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
                  <NexusIcon name="Inbox" size={22} />
                  <span style={{ fontWeight: 600 }}>No support tickets</span>
                  <span className="nx-muted nx-mini">Submit a ticket and our team will respond within 24 hours.</span>
                  <Btn variant="ghost" icon="Plus" onClick={() => setTicketModal(true)}>Raise Ticket</Btn>
                </div>
              ) : (
                <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
                  <thead><tr>
                    <Th icon="Hash">ID</Th>
                    <Th icon="MessageCircle">Subject</Th>
                    <Th icon="Activity">Status</Th>
                    <Th icon="AlertCircle">Priority</Th>
                    <Th icon="Calendar">Created</Th>
                    <Th icon="Clock">Updated</Th>
                    <Th end />
                  </tr></thead>
                  <tbody>{tickets.map(t => (
                    <tr key={t.id} className="nx-tr" style={{ cursor: 'pointer' }} onClick={() => handleViewTicket(t)}>
                      <td><span className="nx-muted nx-mini">#{t.id?.slice(-6) || '—'}</span></td>
                      <td><span className="nx-row-name-text">{t.subject}</span></td>
                      <td><Pill tone={STATUS_TONE[t.status] || 'gray'}>{fmtEnum(t.status)}</Pill></td>
                      <td><Pill tone={PRIORITY_TONE[(t.priority || '').toUpperCase()] || 'gray'}>{fmtEnum(t.priority)}</Pill></td>
                      <td><span className="nx-muted nx-mini">{fmt(t.createdAt)}</span></td>
                      <td><span className="nx-muted nx-mini">{fmt(t.updatedAt)}</span></td>
                      <td className="nx-td-end" onClick={e => e.stopPropagation()}>
                        <Btn variant="ghost" size="sm" icon="MessageCircle" onClick={() => handleViewTicket(t)}>View</Btn>
                      </td>
                    </tr>
                  ))}</tbody>
                </table></div></div>
              )}
            </div>
          )}

          {section === 'downloads' && (
            <div className="nx-empty" style={{ flexDirection: 'column', gap: 6 }}>
              <NexusIcon name="Download" size={22} />
              <span style={{ fontWeight: 600 }}>Downloads</span>
              <span className="nx-muted nx-mini">SDK packages, sample apps, and data export tools will appear here.</span>
            </div>
          )}
        </div>
      </div>

      {ticketDetail && (
        <TenancyModal title={ticketDetail.subject || 'Ticket Detail'} icon="MessageCircle" onClose={() => { setTicketDetail(null); setReplyText(''); }} width={580}>
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <Pill tone={STATUS_TONE[ticketDetail.status] || 'gray'}>{fmtEnum(ticketDetail.status)}</Pill>
              <Pill tone={PRIORITY_TONE[(ticketDetail.priority || '').toUpperCase()] || 'gray'}>{fmtEnum(ticketDetail.priority)}</Pill>
              <span className="nx-muted nx-mini" style={{ alignSelf: 'center' }}>{ticketDetail.category}</span>
              <span className="nx-muted nx-mini" style={{ alignSelf: 'center' }}>#{ticketDetail.id?.slice(-6)}</span>
            </div>
            {ticketDetail.description && (
              <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', padding: '10px 12px', background: 'var(--nx-bg-alt,rgba(0,0,0,0.03))', borderRadius: 6, marginBottom: 12 }}>
                {ticketDetail.description}
              </div>
            )}
            <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 8 }}>MESSAGE THREAD</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto', marginBottom: 12 }}>
              {(ticketDetail.messages || []).length === 0 ? (
                <span className="nx-muted nx-mini">No messages yet.</span>
              ) : (ticketDetail.messages || []).map((m, i) => (
                <div key={m.id || i} style={{ padding: '8px 12px', borderRadius: 6, background: m.fromSupport ? 'var(--nx-accent-tint)' : 'var(--nx-surface-alt)' }}>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 3 }}>{m.fromSupport ? 'Support' : 'You'} · {fmt(m.createdAt)}</div>
                  <div style={{ fontSize: 13 }}>{m.body}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <textarea className="nx-input" rows={2} style={{ flex: 1, resize: 'none' }} placeholder="Type your reply…" value={replyText} onChange={e => setReplyText(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleReply()} />
              <Btn variant="primary" icon="Send" disabled={!replyText.trim() || replying} onClick={handleReply}>{replying ? 'Sending…' : 'Send'}</Btn>
            </div>
          </div>
        </TenancyModal>
      )}

      {ticketModal && (
        <TenancyModal title="Raise Support Ticket" icon="MessageCircle" onClose={() => setTicketModal(false)} width={540}>
          <div className="nx-form" style={{ gap: 10, marginTop: 8 }}>
            <div className="nx-form-row">
              <label>Subject *</label>
              <input className="nx-input" autoFocus value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Brief summary of the issue" />
            </div>
            <div className="nx-form-row">
              <label>Category</label>
              <select className="nx-input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {TICKET_CATEGORY.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="nx-form-row">
              <label>Priority</label>
              <select className="nx-input" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                {TICKET_PRIORITY.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="nx-form-row" style={{ alignItems: 'flex-start' }}>
              <label>Description *</label>
              <textarea className="nx-input" rows={4} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the issue in detail. Include steps to reproduce if applicable." />
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => setTicketModal(false)}>Cancel</Btn>
            <Btn variant="primary" icon="Send" disabled={!form.subject.trim() || !form.description.trim() || submitting} onClick={handleSubmitTicket}>{submitting ? 'Submitting…' : 'Submit Ticket'}</Btn>
          </div>
        </TenancyModal>
      )}
    </div>
  );
}
